import React from 'react';

const TYPE_COLOR = { primary: '#3987e5', secondary: '#9085e9', legacy: '#686868' };

// Truncate for display only — full email is always in the <title> tooltip
// and used for click-through, so nothing is lost, just not drawn past a
// sane width.
const truncate = (text, max = 18) => {
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

// Simple dependency-free relationship graph: accounts placed evenly around a
// circle, lines drawn between accounts that share a fingerprint. No physics
// simulation needed — groups are small (a handful of accounts), so a fixed
// radial layout reads cleanly without pulling in a graphing library.
//
// Labels are truncated AND the SVG has overflow:visible as a second line of
// defense — long emails must never hard-clip against the viewBox edge the
// way they used to (that was the whole bug: "a555search@gmail.com" etc. were
// letters cut off by the SVG boundary, not actually overlapping).
const AccountGraph = ({ accounts, links, onSelectUser, focusUid, size = 320 }) => {
  const n = accounts.length;
  if (n === 0) return null;

  const cx = size / 2;
  const cy = size / 2;
  // Reserve real margin for the label text itself, not just the node —
  // labels extend ~90-110px past the node at font-size 11.
  const labelMargin = Math.min(100, size * 0.32);
  const r = Math.max(40, size / 2 - labelMargin);
  const nodeR = 8;

  const positions = new Map();
  accounts.forEach((a, i) => {
    const angle = (2 * Math.PI * i) / n - Math.PI / 2;
    positions.set(a.id, { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) });
  });

  // One edge per link: connect the link's first account to each other
  // account it lists (a "star" per shared fingerprint) — enough to make
  // every real connection visible without O(n^2) clutter.
  const edges = [];
  links.forEach((link) => {
    const ids = link.accounts.map((a) => a.id).filter((id) => positions.has(id));
    for (let i = 1; i < ids.length; i++) {
      edges.push({ a: ids[0], b: ids[i], type: link.type, hash: link.hash, device: link.device });
    }
  });

  const charW = 6.2; // approx width of an 11px monospace-ish character

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="account-graph-svg"
      style={{ overflow: 'visible' }}
    >
      {edges.map((e, i) => {
        const p1 = positions.get(e.a);
        const p2 = positions.get(e.b);
        if (!p1 || !p2) return null;
        const deviceLabel = [e.device?.browser, e.device?.os].filter(Boolean).join(' / ');
        return (
          <line
            key={i}
            x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
            stroke={TYPE_COLOR[e.type] || '#686868'}
            strokeWidth={1.5}
            opacity={0.6}
          >
            <title>{`${e.type} fingerprint${deviceLabel ? ` — ${deviceLabel}` : ''} (${e.hash.slice(0, 12)}…)`}</title>
          </line>
        );
      })}
      {accounts.map((a) => {
        const p = positions.get(a.id);
        if (!p) return null;
        const isFocus = a.id === focusUid;
        const onRight = p.x >= cx;
        const label = truncate(a.email || a.id, 18);
        const textX = onRight ? nodeR + 6 : -(nodeR + 6);
        const bgW = label.length * charW + 8;
        const bgX = onRight ? textX - 4 : textX - bgW + 4;
        return (
          <g
            key={a.id}
            transform={`translate(${p.x}, ${p.y})`}
            className="account-graph-node"
            onClick={() => onSelectUser && onSelectUser(a.id)}
          >
            <rect x={bgX} y={-9} width={bgW} height={18} rx={4} fill="#0a0a0a" opacity={0.85} />
            <circle r={isFocus ? nodeR + 3 : nodeR} fill={isFocus ? '#3987e5' : '#0f0f0f'} stroke={a.admin ? '#9085e9' : '#3987e5'} strokeWidth={2} />
            <text
              x={textX}
              y={4}
              textAnchor={onRight ? 'start' : 'end'}
              fill="#fff"
              fontSize={11}
            >
              {label}
            </text>
            <title>{a.email || a.id}</title>
          </g>
        );
      })}
    </svg>
  );
};

export default AccountGraph;
